import { useQueryClient } from "@tanstack/react-query";

import { type InvalidateQueryOptions, QueryModule, invalidateQueries } from "@/data/invalidateQueries";
import { useAuthGuardedMutation } from "@/hooks/useAuthGuardedMutation";
import { useAuthGuardedQuery } from "@/hooks/useAuthGuardedQuery";
import type { AppMutationOptions, AppQueryOptions } from "@/types/react-query";

import { BizUserRecentPositionsApi } from "./bizUserRecentPositions.api";
import type { BizUserRecentPositionsModels } from "./bizUserRecentPositions.models";

export namespace BizUserRecentPositionsQueries {
  export const moduleName = QueryModule.BizUserRecentPositions;

  export const keys = {
    all: [moduleName] as const,
    getRecentPositions: () => [...keys.all, "/biz/recent-positions"] as const,
  };

  /**
   * Query `useGetRecentPositions`
   * @summary Get current user recent positions
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizUserRecentPositionsModels.BizRecentPositionResponseDto> }
   * @statusCodes [200]
   */
  export const useGetRecentPositions = <TData>(
    options?: AppQueryOptions<typeof BizUserRecentPositionsApi.getRecentPositions, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.getRecentPositions(),
      queryFn: BizUserRecentPositionsApi.getRecentPositions,
      ...options,
    });
  };

  /**
   * Mutation `useAddRecentPosition`
   * @summary Add a position to recent positions
   * @param { BizUserRecentPositionsModels.AddRecentPositionDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizUserRecentPositionsModels.BizRecentPositionResponseDto> }
   * @statusCodes [200, 201]
   */
  export const useAddRecentPosition = (
    options?: AppMutationOptions<
      typeof BizUserRecentPositionsApi.addRecentPosition,
      { data: BizUserRecentPositionsModels.AddRecentPositionDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizUserRecentPositionsApi.addRecentPosition(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };
}
